import { useDireccionesEntrega } from '../../hooks/useClientes'
import { usePerfilCliente } from '../../hooks/usePerfilCliente'
import { PRINCIPAL_ID } from './SelectorMultiReparto'
import type { DireccionEntregaElegida } from './filas'

/**
 * Selector de dirección de ENTREGA por fila, en el modal "Revisar y publicar"
 * (petición de Iván, oct-2026). Muestra SIEMPRE las dos direcciones con su
 * etiqueta — "Factura" (fija, la de la ficha/matriz) y "Entrega" (un desplegable
 * para elegir a dónde se reparte) — porque en el albarán de Holded aparecen las
 * dos y conviene verlas distintas ya aquí.
 *
 * A diferencia del multi-reparto por checkbox, aquí se elige UNA sola dirección
 * de entrega: la factura NO cambia (misma matriz), solo cambia dónde se entrega.
 * La convención con `aEntradasPublicar` es una lista de 0 o 1 dirección:
 *  - []                → entrega a la dirección principal (la de la ficha).
 *  - [{id, ...}]        → entrega a esa dirección adicional.
 * La id 0 (PRINCIPAL) se normaliza a [] (comportamiento por defecto).
 *
 * Las direcciones adicionales se gestionan en el PERFIL del cliente; aquí solo
 * se eligen. Si el cliente no tiene ninguna adicional, se muestra solo la
 * dirección de facturación, sin desplegable (no hay nada que elegir).
 */
export function SelectorEntregaFila({
  contactId,
  direccionFacturacion,
  elegida,
  onCambiar,
}: {
  contactId: string
  /** La dirección de facturación ya resuelta de la fila (ficha/cliente). */
  direccionFacturacion: string
  elegida: DireccionEntregaElegida[]
  onCambiar: (dirs: DireccionEntregaElegida[]) => void
}) {
  const { data: perfil } = usePerfilCliente(contactId)
  const { data } = useDireccionesEntrega(contactId)
  const adicionales = Array.isArray(data) ? data : []

  const fact = direccionFacturacion || 'Dirección de la ficha'

  // Sin direcciones adicionales no hay nada que elegir: se muestra solo la de
  // facturación, que es a la vez la de entrega.
  if (adicionales.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <span className="inline-flex w-fit rounded bg-elevado px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-tinta-apagada ring-1 ring-borde">
          Factura y entrega
        </span>
        <span className="text-xs text-tinta-2">{fact}</span>
      </div>
    )
  }

  // La dirección principal, construida de la ficha del perfil (id 0).
  const principal: DireccionEntregaElegida = {
    id: PRINCIPAL_ID,
    referencia: 'Dirección principal',
    destinatario: null,
    direccion: perfil?.direccion ?? direccionFacturacion ?? '',
    codigoPostal: perfil?.codigoPostal ?? null,
    ciudad: perfil?.poblacion ?? null,
    provincia: perfil?.provincia ?? null,
  }
  const opciones: DireccionEntregaElegida[] = [
    principal,
    ...adicionales.map((d) => ({
      id: d.id,
      referencia: d.referencia,
      destinatario: d.destinatario,
      direccion: d.direccion,
      codigoPostal: d.codigoPostal,
      ciudad: d.ciudad,
      provincia: d.provincia,
    })),
  ]

  // Qué hay elegido ahora: lista vacía = principal.
  const idElegida = elegida.length === 0 ? PRINCIPAL_ID : elegida[0].id

  const etiquetaOpcion = (op: DireccionEntregaElegida) => {
    if (op.id === PRINCIPAL_ID) return 'Dirección principal (la de la ficha)'
    const ref = op.referencia?.trim()
    const dir = [op.direccion, op.codigoPostal, op.ciudad].filter(Boolean).join(', ')
    return ref ? `${ref} — ${dir}` : dir
  }

  const alElegir = (valor: string) => {
    const id = Number(valor)
    if (id === PRINCIPAL_ID) {
      onCambiar([]) // principal = comportamiento por defecto
      return
    }
    const op = opciones.find((o) => o.id === id)
    onCambiar(op ? [op] : [])
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Facturación: fija, nunca cambia. */}
      <div className="flex items-start gap-1.5">
        <span className="mt-0.5 inline-flex shrink-0 rounded bg-acento-suave px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-acento-tinta ring-1 ring-acento-borde">
          Factura
        </span>
        <span className="text-xs text-tinta-2">{fact}</span>
      </div>

      {/* Entrega: desplegable. */}
      <div className="flex items-start gap-1.5">
        <span className="mt-1.5 inline-flex shrink-0 rounded bg-positivo-fondo px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-positivo-tinta ring-1 ring-positivo-anillo">
          Entrega
        </span>
        <select
          aria-label="Dirección de entrega"
          value={String(idElegida)}
          onChange={(evento) => alElegir(evento.target.value)}
          className="max-w-[16rem] rounded-lg border border-borde-fuerte bg-superficie px-2 py-1 text-xs text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
        >
          {opciones.map((op) => (
            <option key={op.id} value={String(op.id)}>
              {etiquetaOpcion(op)}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
